import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { CoverHero } from "@/pages/admin/GalleryDetail";

import Shell from "./Shell";
import { LoadingState, ErrorState, PasswordGate, SubmittedView } from "./states";
import PhotoGrid from "./PhotoGrid";
import { PhaseBanner, DownloadBar, DownloadProgress } from "./DeliveryBar";
import SelectionBar from "./SelectionBar";
import SubmitModal from "./SubmitModal";
import Lightbox from "./Lightbox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * PublicGallery — orchestrator for the client-facing gallery experience.
 *
 * Owns all client-side state (auth, favorites, downloads, lightbox) and HTTP calls,
 * then delegates rendering to small, focused components in this folder. Behavior is
 * intentionally identical to the pre-refactor single-file version; only the file
 * structure changed so each piece is small enough to maintain.
 */
export default function PublicGallery() {
  const { slug } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [authenticating, setAuthenticating] = useState(false);

  // proofing state
  const [favorites, setFavorites] = useState({}); // photoId -> { favorited: bool, comment: str }
  const [showSubmit, setShowSubmit] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [overallMessage, setOverallMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedResult, setSubmittedResult] = useState(null);

  // download state
  const [downloadPassword, setDownloadPassword] = useState("");
  const [downloadUnlocked, setDownloadUnlocked] = useState(false);
  const [verifyingDl, setVerifyingDl] = useState(false);
  const [zipping, setZipping] = useState(null); // "web" | "original" | null
  const [dlProgress, setDlProgress] = useState(null); // { label, pct: 0-100 | null, bytes }

  // lightbox state
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");

  // ---- Data loading & auth ----
  const loadGallery = useCallback(async (pw) => {
    setLoading(true);
    setError("");
    try {
      const headers = pw ? { "x-gallery-password": pw } : {};
      const { data } = await axios.get(`${API}/galleries/${slug}`, { headers });
      setGallery(data);
      if (pw) {
        try { sessionStorage.setItem(`gallery_pw_${slug}`, pw); }
        catch (err) { console.warn("session pw save failed", err); }
      }
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 404) setError("Gallery not found. The link may be incorrect or expired.");
      else setError(detail || "Couldn't load gallery");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    const stored = (() => {
      try { return sessionStorage.getItem(`gallery_pw_${slug}`); }
      catch (err) { console.warn("session pw read failed", err); return null; }
    })();
    if (stored) {
      setPassword(stored);
      loadGallery(stored);
    } else {
      loadGallery();
    }
  }, [slug, loadGallery]);

  async function submitPassword(e) {
    e?.preventDefault();
    if (!password) return;
    setAuthenticating(true);
    try {
      await axios.post(`${API}/galleries/${slug}/auth`, { password });
      await loadGallery(password);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Incorrect password");
    } finally {
      setAuthenticating(false);
    }
  }

  // ---- Derived ----
  const needsPassword = gallery && gallery.has_password && (!gallery.photos || gallery.photos.length === 0);
  const photos = gallery?.photos || [];
  const favCount = Object.values(favorites).filter((f) => f.favorited).length;
  const includedEdits = gallery?.included_edits || 0;
  const extraPrice = gallery?.extra_edit_price ?? 35;
  const extraCount = includedEdits > 0 ? Math.max(0, favCount - includedEdits) : 0;
  const extraTotal = +(extraCount * extraPrice).toFixed(2);
  const maxSel = gallery?.max_selections || 0;

  // ---- Favorites + comments ----
  function toggleFav(photoId) {
    setFavorites((prev) => {
      const cur = prev[photoId] || { favorited: false, comment: "" };
      const next = { ...cur, favorited: !cur.favorited };
      if (!next.favorited && !next.comment) {
        const { [photoId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [photoId]: next };
    });
  }

  function setComment(photoId, comment) {
    setFavorites((prev) => {
      const cur = prev[photoId] || { favorited: false, comment: "" };
      const next = { ...cur, comment };
      if (!next.favorited && !next.comment) {
        const { [photoId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [photoId]: next };
    });
  }

  async function submitSelections() {
    if (favCount === 0) return toast.error("Favorite at least one photo first");
    if (!clientName.trim() || clientName.trim().length < 2) return toast.error("Please enter your name");
    if (maxSel && favCount > maxSel) return toast.error(`You can pick at most ${maxSel} photos`);

    setSubmitting(true);
    try {
      const items = Object.entries(favorites)
        .filter(([, v]) => v.favorited)
        .map(([photo_id, v]) => ({ photo_id, comment: v.comment || "" }));
      const headers = password ? { "x-gallery-password": password } : {};
      const { data } = await axios.post(
        `${API}/galleries/${slug}/select`,
        {
          items,
          photo_ids: items.map((it) => it.photo_id),
          client_name: clientName.trim(),
          client_email: clientEmail || undefined,
          message: overallMessage || "",
        },
        { headers }
      );
      setSubmittedResult(data);
      setShowSubmit(false);
      toast.success("Selections submitted — your photographer will be in touch!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Download flow ----
  async function verifyDownloadPassword(e) {
    e?.preventDefault();
    setVerifyingDl(true);
    try {
      const headers = password ? { "x-gallery-password": password } : {};
      await axios.post(`${API}/galleries/${slug}/download-auth`, { password: downloadPassword }, { headers });
      setDownloadUnlocked(true);
      try { sessionStorage.setItem(`gallery_dlpw_${slug}`, downloadPassword); }
      catch (err) { console.warn("session dl pw save failed", err); }
      toast.success("Downloads unlocked");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Incorrect password");
    } finally {
      setVerifyingDl(false);
    }
  }

  // load saved dl password
  useEffect(() => {
    if (!gallery?.requires_download_password) {
      if (gallery?.is_delivered) setDownloadUnlocked(true);
      return;
    }
    try {
      const stored = sessionStorage.getItem(`gallery_dlpw_${slug}`);
      if (stored) setDownloadPassword(stored);
    } catch (err) { console.warn("session dl pw read failed", err); }
  }, [gallery, slug]);

  function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function downloadOne(photo) {
    const label = photo.filename || `${photo.id}.jpg`;
    setDlProgress({ label, pct: 0, bytes: 0 });
    try {
      const headers = {};
      if (password) headers["x-gallery-password"] = password;
      if (downloadPassword) headers["x-gallery-download-password"] = downloadPassword;
      const res = await axios.get(`${API}/galleries/${slug}/photos/${photo.id}/download`, {
        headers,
        responseType: "blob",
        onDownloadProgress: (e) => {
          setDlProgress({
            label,
            pct: e.total ? Math.round((e.loaded / e.total) * 100) : null,
            bytes: e.loaded,
          });
        },
      });
      saveBlob(res.data, label);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    } finally {
      setDlProgress(null);
    }
  }

  async function downloadZip(size) {
    setZipping(size);
    const label = size === "web" ? "All photos (web size)" : "All photos (high resolution)";
    setDlProgress({ label, pct: null, bytes: 0 });
    try {
      const headers = {};
      if (password) headers["x-gallery-password"] = password;
      if (downloadPassword) headers["x-gallery-download-password"] = downloadPassword;
      const res = await axios.get(`${API}/galleries/${slug}/zip`, {
        headers,
        responseType: "blob",
        params: { size },
        onDownloadProgress: (e) => {
          setDlProgress({ label, pct: e.total ? Math.round((e.loaded / e.total) * 100) : null, bytes: e.loaded });
        },
      });
      const base = (gallery.name || "gallery").replace(/[^a-z0-9_-]+/gi, "_");
      saveBlob(res.data, `${base}${size === "web" ? "_web" : ""}.zip`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    } finally {
      setZipping(null);
      setDlProgress(null);
    }
  }

  // ---- Lightbox handlers ----
  function openLightbox(idx) {
    setLightboxIdx(idx);
    const p = photos[idx];
    setCommentDraft((p && favorites[p.id]?.comment) || "");
  }
  function closeLightbox() { setLightboxIdx(null); }
  function lbPrev() {
    if (lightboxIdx == null) return;
    const i = (lightboxIdx - 1 + photos.length) % photos.length;
    setLightboxIdx(i);
    setCommentDraft(favorites[photos[i].id]?.comment || "");
  }
  function lbNext() {
    if (lightboxIdx == null) return;
    const i = (lightboxIdx + 1) % photos.length;
    setLightboxIdx(i);
    setCommentDraft(favorites[photos[i].id]?.comment || "");
  }
  function lbSaveComment() {
    if (lightboxIdx == null) return;
    const p = photos[lightboxIdx];
    setComment(p.id, commentDraft);
    toast.success(commentDraft ? "Note saved" : "Note removed");
  }

  // keyboard nav for lightbox
  useEffect(() => {
    function onKey(e) {
      if (lightboxIdx == null) return;
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") lbPrev();
      else if (e.key === "ArrowRight") lbNext();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [lightboxIdx, photos.length]);

  // ---- Render branches ----
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (needsPassword) {
    return (
      <PasswordGate
        gallery={gallery}
        password={password}
        setPassword={setPassword}
        onSubmit={submitPassword}
        authenticating={authenticating}
      />
    );
  }
  if (!gallery) return null;
  if (submittedResult) {
    return <SubmittedView gallery={gallery} clientName={clientName} result={submittedResult} />;
  }

  // ---- Main gallery ----
  const isDelivered = gallery.is_delivered;
  const requiresDlPw = gallery.requires_download_password && !downloadUnlocked;
  const downloadsAvailable = isDelivered && (downloadUnlocked || !gallery.requires_download_password);

  return (
    <Shell
      hero={
        <CoverHero
          layout={gallery.cover_layout || "center"}
          coverImage={gallery.cover_image}
          name={gallery.name}
          studioName={gallery.studio_name}
          customerName={gallery.customer_name}
        />
      }
    >
      {gallery.welcome_message && (
        <div className="text-center mb-10 mt-2" data-testid="gallery-header">
          <p className="max-w-2xl mx-auto text-stone-600 leading-relaxed">{gallery.welcome_message}</p>
        </div>
      )}

      <PhaseBanner isDelivered={isDelivered} includedEdits={includedEdits} extraPrice={extraPrice} />

      {isDelivered && (
        <DownloadBar
          requiresDlPw={requiresDlPw}
          downloadPassword={downloadPassword}
          setDownloadPassword={setDownloadPassword}
          onVerify={verifyDownloadPassword}
          verifyingDl={verifyingDl}
          zipping={zipping}
          onZip={downloadZip}
        />
      )}

      <PhotoGrid
        photos={photos}
        favorites={favorites}
        isDelivered={isDelivered}
        downloadsAvailable={downloadsAvailable}
        onOpenLightbox={openLightbox}
        onToggleFav={toggleFav}
        onDownloadOne={downloadOne}
      />

      {!isDelivered && favCount > 0 && (
        <SelectionBar
          favCount={favCount}
          includedEdits={includedEdits}
          extraCount={extraCount}
          extraTotal={extraTotal}
          maxSel={maxSel}
          onOpenSubmit={() => setShowSubmit(true)}
        />
      )}

      {showSubmit && (
        <SubmitModal
          favCount={favCount}
          includedEdits={includedEdits}
          extraCount={extraCount}
          extraPrice={extraPrice}
          extraTotal={extraTotal}
          clientName={clientName}
          setClientName={setClientName}
          clientEmail={clientEmail}
          setClientEmail={setClientEmail}
          overallMessage={overallMessage}
          setOverallMessage={setOverallMessage}
          submitting={submitting}
          onClose={() => setShowSubmit(false)}
          onSubmit={submitSelections}
        />
      )}

      {lightboxIdx != null && photos[lightboxIdx] && (
        <Lightbox
          photo={photos[lightboxIdx]}
          isFav={!!favorites[photos[lightboxIdx].id]?.favorited}
          comment={commentDraft}
          setComment={setCommentDraft}
          onSaveComment={lbSaveComment}
          onToggleFav={() => toggleFav(photos[lightboxIdx].id)}
          onClose={closeLightbox}
          onPrev={lbPrev}
          onNext={lbNext}
          mode={isDelivered ? "delivery" : "proofing"}
          downloadsAvailable={downloadsAvailable}
          onDownload={() => downloadOne(photos[lightboxIdx])}
          idx={lightboxIdx}
          total={photos.length}
        />
      )}

      <DownloadProgress progress={dlProgress} />
    </Shell>
  );
}
